import { ImageDerivativeManifest } from "./imageDerivatives";

export const MAX_PANORAMA_MARGIN_PERCENT = 49;

export type PanoramaAspectRatio = {
  width: number;
  height: number;
};

export type PanoramaAspectRatioRange = {
  min: number;
  max: number;
};

export type PanoramaRequirements = {
  PHOTO?: {
    min_aspect_ratio?: number;
    max_aspect_ratio?: number;
    max_resolution?: number;
    max_items?: number;
  };
  total_max_items?: number;
};

export type PanoramaSliceOptions = {
  count: number;
};

export type PanoramaBackgroundOptions = {
  color: "black" | "white";
  horizontalMarginPercent: number;
  verticalMarginPercent: number;
  aspectRatio: PanoramaAspectRatio;
  position: "before" | "after";
};

export type GeneratePanoramaRequest = {
  userId?: string;
  generationId: string;
  outputIndex: number;
  originalPath: string;
  feedId: number;
  slices?: PanoramaSliceOptions;
  background?: PanoramaBackgroundOptions;
};

export type GeneratedPanoramaAsset = {
  kind: "slice" | "background";
  name: string;
  storageId: string;
  mimeType: string;
  thumbnailUrl: string;
  imageDerivatives: ImageDerivativeManifest;
};

export type GeneratePanoramaResponse = {
  assets: GeneratedPanoramaAsset[];
};

export type PanoramaInfoRequest = {
  userId?: string;
  originalPath: string;
  feedId: number;
};

export type PanoramaInfoResponse = {
  sourceWidth: number;
  sourceHeight: number;
  aspectRatioRange: PanoramaAspectRatioRange;
  itemLimit: number;
  maxOutputEdge: number;
};

export type CleanupPanoramaRequest = {
  userId?: string;
  generationId: string;
};

export type CleanupPanoramaResponse = {
  deletedAssets: number;
};

function toPositiveNumber(value: unknown) {
  const parsed = typeof value === "number" ? value : Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

export function getPanoramaAspectRatioRange(
  requirements: PanoramaRequirements | null | undefined,
): PanoramaAspectRatioRange | null {
  const min = toPositiveNumber(requirements?.PHOTO?.min_aspect_ratio);
  const max = toPositiveNumber(requirements?.PHOTO?.max_aspect_ratio);

  if (min === null || max === null || min > max) return null;
  return { min, max };
}

export function getPanoramaItemLimit(
  requirements: PanoramaRequirements | null | undefined,
) {
  const limits = [
    toPositiveNumber(requirements?.PHOTO?.max_items),
    toPositiveNumber(requirements?.total_max_items),
  ].filter((limit): limit is number => limit !== null);

  if (limits.length === 0) return null;
  return Math.floor(Math.min(...limits));
}

export function getPanoramaMaxOutputEdge(
  requirements: PanoramaRequirements | null | undefined,
) {
  const configured = toPositiveNumber(requirements?.PHOTO?.max_resolution);
  return configured === null ? null : Math.max(2, Math.floor(configured));
}

export function getValidPanoramaSliceCounts({
  sourceWidth,
  sourceHeight,
  requirements,
  includeBackground,
}: {
  sourceWidth: number;
  sourceHeight: number;
  requirements: PanoramaRequirements | null | undefined;
  includeBackground: boolean;
}) {
  const range = getPanoramaAspectRatioRange(requirements);
  const itemLimit = getPanoramaItemLimit(requirements);

  if (
    !range ||
    itemLimit === null ||
    !Number.isFinite(sourceWidth) ||
    !Number.isFinite(sourceHeight) ||
    sourceWidth <= 0 ||
    sourceHeight <= 0
  ) {
    return [];
  }

  const maxSlices = itemLimit - (includeBackground ? 1 : 0);
  const counts: number[] = [];

  for (let count = 2; count <= maxSlices; count++) {
    const sliceRatio = sourceWidth / count / sourceHeight;
    if (sliceRatio >= range.min && sliceRatio <= range.max) {
      counts.push(count);
    }
  }

  return counts;
}

export function isPanoramaAspectRatioValid(
  aspectRatio: PanoramaAspectRatio,
  range: PanoramaAspectRatioRange,
) {
  const width = toPositiveNumber(aspectRatio.width);
  const height = toPositiveNumber(aspectRatio.height);
  if (width === null || height === null) return false;

  const ratio = width / height;
  return ratio >= range.min && ratio <= range.max;
}

export function isPanoramaMarginValid(value: number) {
  return (
    Number.isFinite(value) && value >= 0 && value <= MAX_PANORAMA_MARGIN_PERCENT
  );
}
